import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setToken, getToken, clearToken } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { Loader2, LogOut, Check, X, Trash2, Plus } from "lucide-react";

const fmt = (s) =>
  s
    ? new Date(s).toLocaleDateString("en-NZ", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

export default function AdminPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      setChecking(false);
      return;
    }
    let mounted = true;
    api.get("/auth/me").then(() => {
      if (mounted) {
        setAuthed(true);
        setChecking(false);
      }
    }).catch(() => {
      clearToken();
      if (mounted) {
        setChecking(false);
      }
    });
    return () => { mounted = false; };
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return authed ? (
    <AdminDashboard onLogout={() => { clearToken(); setAuthed(false); navigate("/admin"); }} />
  ) : (
    <AdminLogin onLogin={() => setAuthed(true)} />
  );
}

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      setToken(res.data.token);
      toast.success("Welcome back");
      onLogin();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--marble))]/40 flex items-center justify-center p-6">
      <form
        onSubmit={submit}
        className="bg-[hsl(var(--card))] rounded-2xl p-10 max-w-md w-full border border-[hsl(var(--border))]"
      >
        <div className="font-serif-logo text-3xl tracking-[0.4em] mb-2 text-[hsl(var(--ink))]">ATA</div>
        <h1 className="editorial-heading text-2xl mb-2">Owners&apos; dashboard</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-8">
          Sign in to manage bookings and availability.
        </p>

        <label className="block mb-4">
          <span className="tracking-eyebrow text-[10px] text-[hsl(var(--muted-foreground))] block mb-2">Email</span>
          <input
            data-testid={ADMIN.loginEmail}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="ata-input w-full"
          />
        </label>
        <label className="block mb-6">
          <span className="tracking-eyebrow text-[10px] text-[hsl(var(--muted-foreground))] block mb-2">Password</span>
          <input
            data-testid={ADMIN.loginPassword}
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="ata-input w-full"
          />
        </label>
        <button
          data-testid={ADMIN.loginSubmit}
          disabled={loading}
          className="btn-pill btn-dark w-full"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}
        </button>

        <style>{`
          .ata-input {
            background: hsl(var(--card));
            border: 1px solid hsl(var(--border));
            border-radius: 0.875rem;
            padding: 0.85rem 1rem;
            font-size: 0.95rem;
            color: hsl(var(--ink));
          }
          .ata-input:focus { outline: none; border-color: hsl(var(--sage-deep)); }
        `}</style>
      </form>
    </div>
  );
}

function AdminDashboard({ onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [settings, setSettings] = useState({ nightly_rate: 450, currency: "NZD", min_nights: 3 });
  const [loading, setLoading] = useState(true);
  const [rate, setRate] = useState("");
  const [minNights, setMinNights] = useState("");
  const [blockStart, setBlockStart] = useState("");
  const [blockEnd, setBlockEnd] = useState("");
  const [blockReason, setBlockReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [b, bl, st] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/blocked"),
        api.get("/settings"),
      ]);
      setBookings(b.data.items);
      setBlocked(bl.data.items);
      setSettings(st.data);
      setRate(String(st.data.nightly_rate));
      setMinNights(String(st.data.min_nights));
    } catch (e) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveRate = async () => {
    try {
      await api.put("/admin/settings", {
        nightly_rate: Number(rate),
        min_nights: Number(minNights),
      });
      toast.success("Settings updated");
      load();
    } catch (e) {
      toast.error("Could not update settings");
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/approve`);
      toast.success("Booking approved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const decline = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/decline`);
      toast.success("Booking declined");
      load();
    } catch (e) { toast.error("Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const addBlocked = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/blocked", { start: blockStart, end: blockEnd, reason: blockReason });
      setBlockStart(""); setBlockEnd(""); setBlockReason("");
      toast.success("Dates blocked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeBlocked = async (id) => {
    try {
      await api.delete(`/admin/blocked/${id}`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  const pending = bookings.filter((b) => b.status === "pending");
  const confirmed = bookings.filter((b) => b.status === "confirmed");
  const declined = bookings.filter((b) => b.status === "declined");

  return (
    <div className="min-h-screen bg-[hsl(var(--marble))]/30">
      <header className="border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]">
        <div className="container-editorial flex items-center justify-between h-16">
          <div className="font-serif-logo text-2xl tracking-[0.4em]">ATA · Admin</div>
          <button data-testid={ADMIN.logout} onClick={onLogout} className="text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 hover:opacity-70">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="container-editorial py-12 space-y-14">
        {/* Settings */}
        <section>
          <h2 className="font-display text-2xl mb-6">Nightly rate &amp; settings</h2>
          <div className="bg-[hsl(var(--card))] rounded-2xl p-6 border border-[hsl(var(--border))] grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <label className="block">
              <span className="tracking-eyebrow text-[10px] text-[hsl(var(--muted-foreground))] block mb-2">Nightly rate ({settings.currency})</span>
              <input
                data-testid={ADMIN.rateInput}
                type="number"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="ata-input w-full"
              />
            </label>
            <label className="block">
              <span className="tracking-eyebrow text-[10px] text-[hsl(var(--muted-foreground))] block mb-2">Minimum nights</span>
              <input
                type="number"
                value={minNights}
                onChange={(e) => setMinNights(e.target.value)}
                className="ata-input w-full"
              />
            </label>
            <button data-testid={ADMIN.rateSave} onClick={saveRate} className="btn-pill btn-dark">
              Save
            </button>
          </div>
        </section>

        {/* Blocked ranges */}
        <section>
          <h2 className="font-display text-2xl mb-6">Block out dates</h2>
          <form onSubmit={addBlocked} className="bg-[hsl(var(--card))] rounded-2xl p-6 border border-[hsl(var(--border))] grid grid-cols-1 md:grid-cols-4 gap-4 items-end mb-6">
            <label className="block">
              <span className="tracking-eyebrow text-[10px] block mb-2">Start</span>
              <input
                required
                type="date"
                value={blockStart}
                onChange={(e) => setBlockStart(e.target.value)}
                data-testid={ADMIN.blockedStart}
                className="ata-input w-full"
              />
            </label>
            <label className="block">
              <span className="tracking-eyebrow text-[10px] block mb-2">End</span>
              <input
                required
                type="date"
                value={blockEnd}
                onChange={(e) => setBlockEnd(e.target.value)}
                data-testid={ADMIN.blockedEnd}
                className="ata-input w-full"
              />
            </label>
            <label className="block md:col-span-1">
              <span className="tracking-eyebrow text-[10px] block mb-2">Reason</span>
              <input
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                data-testid={ADMIN.blockedReason}
                placeholder="Family use / Airbnb"
                className="ata-input w-full"
              />
            </label>
            <button data-testid={ADMIN.blockedAdd} className="btn-pill btn-dark"><Plus size={14} className="mr-1" /> Block</button>
          </form>

          {blocked.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No date blocks yet.</p>
          ) : (
            <div className="space-y-2">
              {blocked.map((b) => (
                <div key={b.id} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl px-5 py-3 flex items-center justify-between text-sm">
                  <div>
                    <span className="font-display text-base">{fmt(b.start)} → {fmt(b.end)}</span>
                    {b.reason && <span className="ml-3 text-[hsl(var(--muted-foreground))]">· {b.reason}</span>}
                  </div>
                  <button
                    data-testid={ADMIN.blockedRemove(b.id)}
                    onClick={() => removeBlocked(b.id)}
                    className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Bookings */}
        <BookingList title={`Pending (${pending.length})`} items={pending} onApprove={approve} onDecline={decline} onDelete={remove} highlight />
        <BookingList title={`Confirmed (${confirmed.length})`} items={confirmed} onApprove={approve} onDecline={decline} onDelete={remove} />
        <BookingList title={`Declined (${declined.length})`} items={declined} onApprove={approve} onDecline={decline} onDelete={remove} />
      </main>

      <style>{`
        .ata-input {
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 0.875rem;
          padding: 0.65rem 0.85rem;
          font-size: 0.95rem;
          color: hsl(var(--ink));
        }
        .ata-input:focus { outline: none; border-color: hsl(var(--sage-deep)); }
      `}</style>
    </div>
  );
}

function BookingList({ title, items, onApprove, onDecline, onDelete, highlight }) {
  return (
    <section>
      <h2 className="font-display text-2xl mb-6">{title}</h2>
      {items.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))]">No bookings.</p>
      ) : (
        <div className="space-y-3">
          {items.map((b) => (
            <div
              key={b.id}
              className={`rounded-2xl border ${highlight ? "border-[hsl(var(--sage-deep))]/40 bg-[hsl(var(--sage))]/10" : "border-[hsl(var(--border))] bg-[hsl(var(--card))]"} p-5 md:p-6`}
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                <div className="md:col-span-3">
                  <div className="font-display text-lg">{b.name}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{b.email}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{b.phone}</div>
                </div>
                <div className="md:col-span-3">
                  <div className="tracking-eyebrow text-[10px] text-[hsl(var(--muted-foreground))] mb-1">Dates</div>
                  <div className="text-sm">{fmt(b.check_in)} → {fmt(b.check_out)}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{b.nights} nights · {b.guests} guests</div>
                </div>
                <div className="md:col-span-2">
                  <div className="tracking-eyebrow text-[10px] text-[hsl(var(--muted-foreground))] mb-1">Total</div>
                  <div className="font-display text-lg">NZD ${b.total?.toLocaleString()}</div>
                </div>
                <div className="md:col-span-4 flex flex-wrap gap-2 md:justify-end">
                  {b.status !== "confirmed" && (
                    <button data-testid={ADMIN.bookingApprove(b.id)} onClick={() => onApprove(b.id)} className="btn-pill btn-dark text-xs">
                      <Check size={14} className="mr-1" /> Approve
                    </button>
                  )}
                  {b.status !== "declined" && (
                    <button data-testid={ADMIN.bookingDecline(b.id)} onClick={() => onDecline(b.id)} className="btn-pill bg-[hsl(var(--marble))] text-[hsl(var(--ink))] text-xs hover:bg-[hsl(var(--muted))]">
                      <X size={14} className="mr-1" /> Decline
                    </button>
                  )}
                  <button data-testid={ADMIN.bookingDelete(b.id)} onClick={() => onDelete(b.id)} className="btn-pill text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {b.message && (
                <div className="mt-4 pt-4 border-t border-[hsl(var(--border))] text-sm text-[hsl(var(--muted-foreground))] italic">
                  &ldquo;{b.message}&rdquo;
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
